import { camelize } from "humps";
import { ContentStatus, ContentType } from "../components/ContentCard/contentCardTypes";
import type { PathDetailResponse } from "../../api/user";

interface PathDetails {
  id: string;
  enrolledInPath: boolean;
  enrolledInLevel: boolean;
  activated: boolean;
  status: string;
  title: string;
  description: string;
  pointsEarned?: number;
  totalPoints?: number;
  lessonsCompleted: number;
  lessonsInPath: number;
  estimatedTimeToComplete: string;
  estimatedTimeRemaining: string;
  levelId: number;
  reassigned?: boolean;
}

interface TransformedLesson {
  id: number;
  type: string;
  contentType: ContentType;
  title: string;
  description: string;
  estimatedTime: number;
  difficulty: string;
  status: ContentStatus;
  lessonKey: string;
  points?: number;
  required?: boolean;
}

interface OrganizedResponse {
  pathDetails: PathDetails;
  remainingLessons: TransformedLesson[];
  completedLessons: TransformedLesson[];
}

export function transformPathData(apiResponse: PathDetailResponse): OrganizedResponse {
  const { pathDetails, lessons } = apiResponse;

  // Separate lessons into completed and remaining
  const completedLessons: TransformedLesson[] = [];
  const inProgressLessons: TransformedLesson[] = [];
  const notStartedLessons: TransformedLesson[] = [];

  lessons.forEach(lesson => {
    // Normalize status to ContentStatus type
    const normalizedStatus: ContentStatus =
      lesson.status === "in_progress" || lesson.status === "inProgress"
        ? "inProgress"
        : lesson.status === "not_started" || lesson.status === "incomplete"
          ? "notStarted"
          : "completed";

    const transformedLesson: TransformedLesson = {
      id: lesson.lessonId,
      type: lesson.type,
      contentType: camelize(lesson.contentType) as ContentType,
      title: lesson.title,
      description: lesson.description,
      estimatedTime: lesson.estimatedTime,
      difficulty: lesson.difficulty,
      status: normalizedStatus,
      lessonKey: lesson.lessonKey,
      required: lesson.required,
      ...(lesson.points !== undefined && { points: lesson.points }),
    };

    if (normalizedStatus === "completed") {
      completedLessons.push(transformedLesson);
    } else if (normalizedStatus === "inProgress") {
      inProgressLessons.push(transformedLesson);
    } else {
      notStartedLessons.push(transformedLesson);
    }
  });

  // Combine in-progress lessons first, then not-started lessons
  const remainingLessons = [...inProgressLessons, ...notStartedLessons];

  return {
    pathDetails: {
      id: pathDetails.id.toString(),
      title: pathDetails.title,
      description: pathDetails.description,
      lessonsCompleted: pathDetails.lessonsCompleted,
      lessonsInPath: pathDetails.lessonsInPath,
      estimatedTimeToComplete: pathDetails.estimatedTimeToComplete.toString(),
      estimatedTimeRemaining: pathDetails.estimatedTimeRemaining.toString(),
      pointsEarned: completedLessons.length * 100,
      totalPoints: lessons.length * 100,
      levelId: pathDetails.levelId,
      status: pathDetails.status,
      enrolledInPath: pathDetails.enrolledInPath,
      enrolledInLevel: pathDetails.enrolledInLevel,
      activated: pathDetails.activated,
      reassigned: pathDetails.reassigned,
    },
    remainingLessons,
    completedLessons,
  };
}

export const formatDuration = (totalMinutes?: number): string => {
  if (!totalMinutes || totalMinutes <= 0) return "0 mins";

  if (totalMinutes < 60) return `${totalMinutes} mins`;

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (remainingMinutes === 0) return `${hours} hr${hours > 1 ? "s" : ""}`;

  return `${hours} hr${hours > 1 ? "s" : ""} ${remainingMinutes} mins`;
};
